import { useState } from 'owa-react';
import React from 'react';
import classNames from 'owa-classnames';
import { Virtuoso } from 'react-virtuoso';
import type { VirtuosoHandle, ListRange } from 'react-virtuoso';
import { observer } from 'owa-mobx-react';
import type {
    VirtualizedLoadMoreListViewProps,
    VirtualizedLoadMoreListViewRef,
} from './VirtualizedLoadMoreListView.tsx';
import { rowWrapper, listAutoSizer, row, minHeightRow } from './VirtualizedLoadMoreListView.scss';
import { useElementSizeTracker } from 'owa-react-hooks/lib/useElementSizeTracker';
import { isCurrentCultureRightToLeft } from 'owa-localize';
import { safeRequestAnimationFrame, wrapForcedLayout } from 'owa-performance';
import { getListViewState } from 'owa-mail-list-store';
import endMailListLoadDatapoint from 'owa-mail-logging/lib/utils/mailListLoad/endMailListLoadDatapoint';

// PAGINATION_PREFETCH_BUFFER defines the size of the preload pagination buffer
// height as a factor of the viewport height. For example, if the viewport height
// is 1000px and the PAGINATION_PREFETCH_BUFFER is 2, then the preload buffer
// height will be 2000px. When the user scrolls within 2000px of the bottom
// of the list, props.onLoadMore will be called to begin prefetching the next
// "page" of rows. Akin to LoadMoreListView's DEFAULT_GUARD_PAGE_COUNT.
const PAGINATION_PREFETCH_BUFFER = 2;

const defaultExport: React.MemoExoticComponent<
    React.ForwardRefExoticComponent<
        Omit<
            VirtualizedLoadMoreListViewProps<any, any> &
                React.RefAttributes<VirtualizedLoadMoreListViewRef>,
            'ref'
        > &
            React.RefAttributes<VirtualizedLoadMoreListViewRef>
    >
> = observer(
    React.forwardRef<
        VirtualizedLoadMoreListViewRef,
        VirtualizedLoadMoreListViewProps<any, any> // this shouldn't be 'any' but couldn't figure out how to do the types
    >(function VirtuosoLoadMoreListView<TListProps, TSubComponentProps>(
        props: VirtualizedLoadMoreListViewProps<TListProps, TSubComponentProps>,
        ref: React.Ref<VirtualizedLoadMoreListViewRef>
    ) {
        const {
            className,
            currentLoadedIndex,
            estimatedRowHeight,
            getCanLoadMore,
            getCanLoadMorePrevious,
            isLoadRowsInProgress,
            onLoadMoreRows,
            onScroll,
            focusedRowKeyIndex,
            focusedNodeId,
            activeAnimationsCount,
            itemIds,
            listProps,
            loadingComponent,
            onRenderRow,
            onRenderHeader,
            PreRowsComponent,
            MidRowsComponent,
            midRowsComponentRowNumber,
            PostRowsComponent,
            subComponentProps,
            hiddenRowIndices,
            focusedRowKey,
        } = props;

        const virtuosoRef = React.useRef<VirtuosoHandle>(null);
        const scrollingRegionRef = React.useRef<HTMLElement | Window | null>(null);
        const listAutoSizerRef = React.useRef<HTMLDivElement>(null);
        const skipScrollToFocusedRow = React.useRef<boolean>(false);
        const [isScrollingValue, setIsScrollingValue] = useState(false);
        const listViewState = getListViewState();

        React.useEffect(() => {
            // On page load and folder switches, skip the scroll to focused row logic because we want to stay scrolled
            // at the top of the list for the PreRowsComponent (ads list) and pinned items.
            skipScrollToFocusedRow.current = true;
            safeRequestAnimationFrame(() => {
                endMailListLoadDatapoint(
                    'VML' /*componentRendered*/,
                    props.dataSourceId /*tableViewId*/
                );
                const scrollingRegionRefVal = scrollingRegionRef.current;
                wrapForcedLayout('VirtuosoLoadMoreListView_scrollTop', () => {
                    if (scrollingRegionRefVal) {
                        /* eslint-disable-next-line no-restricted-properties -- (https://aka.ms/OWALintWiki)
                     Justification: On page load and folder switches, we want to scroll to the top of the list.
                     This is done by setting the scrollTop of the scrolling region to 0 */
                        (scrollingRegionRefVal as HTMLDivElement).scrollTop = 0;
                    }
                });
                skipScrollToFocusedRow.current = false; // reset back to false after the scrollTop has been set.
            });
        }, [props.dataSourceId]);
        React.useImperativeHandle(
            ref,
            () => ({
                getScrollRegion: () => scrollingRegionRef.current as HTMLDivElement,
                setFocus: () => scrollingRegionRef.current?.focus(),
                scrollToIndex: (
                    numPreviousRowsLoaded: number,
                    _firstNewRowInstanceKey: string | undefined,
                    isLoadingPreviousAfterInitialJump: boolean
                ) => {
                    // On the first load previous after a jump, we want to scroll to the focused row. This ensures that
                    // if we jump to the end of the list via the "End" key, we will scroll to the last row in the list rather than
                    // the previous loadedStartIndex (aka the row at index numPreviousRowsLoaded).
                    if (isLoadingPreviousAfterInitialJump) {
                        const focusedRowIndex = focusedRowKey
                            ? props.itemIds.indexOf(focusedRowKey)
                            : focusedRowKeyIndex;

                        if (focusedRowIndex !== undefined) {
                            virtuosoRef.current?.scrollToIndex({
                                index: focusedRowIndex,
                                align: 'start',
                                behavior: 'auto',
                            });
                        }
                    } else {
                        // On subsequent load previous calls, we want to scroll to wherever the list was previously scrolled to after we have loaded
                        // more rows above. That's why we add startIndex to numPreviousRowsLoaded. startIndex is the index of the row that
                        // was previously scrolled to before the load more rows call.
                        const startIndex = listVisibleRange.current.startIndex;
                        virtuosoRef.current?.scrollToIndex({
                            index: numPreviousRowsLoaded + startIndex,
                            align: 'start',
                            behavior: 'auto',
                        });
                    }
                },
            }),
            [props.itemIds, focusedRowKey, focusedRowKeyIndex]
        );

        const isRtl = isCurrentCultureRightToLeft();
        const listStyle: React.CSSProperties = React.useMemo(() => {
            return isRtl
                ? {
                      direction: 'rtl',
                  }
                : {};
        }, [isRtl]);

        const [listAutoSizerRect] = useElementSizeTracker(
            'VirtualizedLoadMoreListView_ET',
            listAutoSizerRef
        );

        const listVisibleRange = React.useRef<ListRange>({
            startIndex: 0,
            endIndex: 0,
        });

        const onRangeChanged = React.useCallback(
            (range: ListRange) => {
                const { startIndex, endIndex } = range;
                listVisibleRange.current = range;

                // Calculate the number of hidden rows in the visible range.
                let numHiddenRows = 0;
                for (const index of hiddenRowIndices) {
                    if (index + 1 >= startIndex && index + 1 <= endIndex) {
                        numHiddenRows++;
                    }
                }

                // If the end of the fetched list is visible and we can load more rows below, we should prioritize loading more rows in the next page.
                const areAllItemsAtBottomVisibleAndCanFetchNext =
                    currentLoadedIndex - 1 === endIndex && getCanLoadMore();

                const bufferedElements = Math.max(
                    (endIndex - startIndex - numHiddenRows) * PAGINATION_PREFETCH_BUFFER,
                    0
                );
                const nearVisibleEnd = currentLoadedIndex - endIndex < bufferedElements;
                const nearVisibleStart = startIndex === 0;

                if (
                    !areAllItemsAtBottomVisibleAndCanFetchNext &&
                    nearVisibleStart &&
                    getCanLoadMorePrevious() &&
                    activeAnimationsCount === 0
                ) {
                    onLoadMoreRows('PreviousPage');
                }

                if (nearVisibleEnd && getCanLoadMore() && activeAnimationsCount === 0) {
                    onLoadMoreRows('NextPage');
                }
            },
            [
                currentLoadedIndex,
                getCanLoadMore,
                getCanLoadMorePrevious,
                onLoadMoreRows,
                activeAnimationsCount,
                hiddenRowIndices,
            ]
        );

        const renderHeaderAboveRow = React.useCallback(
            (rowToRender: number): JSX.Element | null => {
                if (onRenderHeader) {
                    const previousElementId =
                        rowToRender > 0 && rowToRender <= currentLoadedIndex
                            ? itemIds[rowToRender - 1]
                            : null;
                    const currentElementId = itemIds[rowToRender];
                    return onRenderHeader(previousElementId, currentElementId);
                }
                return null;
            },
            [onRenderHeader, currentLoadedIndex, itemIds]
        );

        const rowStyle: React.CSSProperties = React.useMemo(() => {
            return isScrollingValue
                ? {
                      pointerEvents: 'none',
                  }
                : {};
        }, [isScrollingValue]);

        const renderRow = React.useCallback(
            (index: number, rowKey: string): JSX.Element | null => {
                if (index < 0 || index >= itemIds.length) {
                    return null;
                }

                const isRowInCollapsedGroup = hiddenRowIndices.includes(index);

                const isNotVisible =
                    isRowInCollapsedGroup ||
                    index < listVisibleRange.current.startIndex ||
                    index > listVisibleRange.current.endIndex;

                const rowToRender = onRenderRow(
                    rowKey,
                    index,
                    listProps,
                    // render minimum if we are scrolling or the row is not visible
                    isScrollingValue || isNotVisible
                );
                const header = renderHeaderAboveRow(index);
                const midRowsComponent =
                    index === midRowsComponentRowNumber && MidRowsComponent ? (
                        <MidRowsComponent {...(subComponentProps as any)} />
                    ) : null;

                const loadingSpinnerComponent =
                    index === itemIds.length - 1 && isLoadRowsInProgress && loadingComponent;

                if (
                    header ||
                    midRowsComponent ||
                    rowToRender /* used to be null, now <></> */ ||
                    loadingSpinnerComponent
                ) {
                    return (
                        <div
                            data-animatable={true}
                            className={classNames(row, !isRowInCollapsedGroup && minHeightRow)}
                            style={rowStyle}
                        >
                            {header}
                            {midRowsComponent}
                            {rowToRender}
                            {/* The last row gets a loading spinner if we're actively loading more data */}
                            {loadingSpinnerComponent}
                        </div>
                    );
                } else {
                    return !isRowInCollapsedGroup ? <div className={minHeightRow}></div> : null;
                }
            },
            [
                isLoadRowsInProgress,
                loadingComponent,
                itemIds,
                onRenderRow,
                midRowsComponentRowNumber,
                MidRowsComponent,
                isScrollingValue,
                hiddenRowIndices.length,
            ]
        );

        // When the tableView's focusedRowKey changes, we want to trigger this useEffect to perform a calculation
        // to determine whether the new focusedRowKey is in view (between the visible indices).
        // If it is not within the current visible indices, we want to scroll to that item.
        React.useEffect(() => {
            const startIndex = listViewState.startIndexVLV;
            const endIndex = listViewState.endIndexVLV;

            if (
                focusedRowKeyIndex !== null &&
                focusedRowKeyIndex !== undefined &&
                focusedRowKeyIndex !== -1 &&
                !(startIndex === 0 && endIndex === 0) && // If the start and end indices are both 0, the VML is either empty or hasn't completed loading so we don't want to scroll.
                !skipScrollToFocusedRow.current // If skipScrollToFocusedRow is true, we don't want to scroll to the focused row.
            ) {
                const indexToScrollTo = focusedRowKeyIndex;
                const isRowAboveStartIndex = indexToScrollTo < startIndex;
                const isRowBelowEndIndex = indexToScrollTo > endIndex;
                if (isRowAboveStartIndex || isRowBelowEndIndex) {
                    virtuosoRef.current?.scrollToIndex({
                        index: indexToScrollTo,
                        align: isRowAboveStartIndex ? 'start' : 'end',
                    });
                }
            }
        }, [focusedRowKeyIndex, focusedNodeId]);

        const handleScrollerRef = React.useCallback((scrollRef: HTMLElement | Window | null) => {
            scrollingRegionRef.current = scrollRef;
        }, []);

        const onListScroll = React.useCallback(
            (isScrolling: boolean) => {
                setIsScrollingValue(isScrolling);
                const scrollingRegion = scrollingRegionRef.current as HTMLDivElement;
                if (isScrolling && scrollingRegion && onScroll) {
                    onScroll(scrollingRegion);
                }
            },
            [onScroll]
        );

        const virtuosoComponents = React.useMemo(
            () => ({
                Header: () =>
                    PreRowsComponent ? <PreRowsComponent {...(subComponentProps as any)} /> : null,
                Footer: () =>
                    PostRowsComponent ? (
                        <PostRowsComponent {...(subComponentProps as any)} />
                    ) : null,
            }),
            [PreRowsComponent, PostRowsComponent, subComponentProps]
        );

        return (
            <div className={classNames(rowWrapper, props.rowWrapperClass)}>
                <div ref={listAutoSizerRef} className={listAutoSizer}>
                    <Virtuoso
                        components={virtuosoComponents}
                        className={className}
                        scrollerRef={handleScrollerRef}
                        ref={virtuosoRef}
                        style={listStyle}
                        data={itemIds}
                        width={listAutoSizerRect?.width || 0}
                        height={listAutoSizerRect?.height || 0}
                        itemContent={renderRow}
                        rangeChanged={onRangeChanged}
                        increaseViewportBy={200}
                        totalCount={itemIds.length}
                        defaultItemHeight={estimatedRowHeight}
                        isScrolling={onListScroll}
                        skipAnimationFrameInResizeObserver={true}
                        computeItemKey={computeItemKey}
                    />
                </div>
            </div>
        );
    }),
    'VirtuosoLoadMoreListView'
);
export default defaultExport;

function computeItemKey(_: number, itemId: string) {
    return itemId;
}
